import { components, paths } from "@api/api";
import useAxios from "@hooks/useFetch";
import { useMemo } from "react";
import { KeywordBase, KeywordTableColumn, useGetKeywordBaseData } from "@api/useGetKeywordBaseData";
import { AffinityAmong, HOME_GAIN_AFFINITY_ORDER } from "@models/Metric";

const REQUEST_PATH: keyof paths = '/api/Search/GetGain';
export type HomeGainRequestPayload = components["schemas"]["FlavorTracker.Entities.DTO.SearchPage.SearchPageMetricRequest"];
type ResponseData = components["schemas"]["FlavorTracker.Entities.DTO.SearchPage.SearchPageGainResponse"][];

export type HomeGainDataItem = KeywordBase & {
    base: number;
    columns: Map<string, KeywordTableColumn<number>>;
}

export function useHomeGainData(payload: HomeGainRequestPayload) {
    const getKeywordBaseData = useGetKeywordBaseData();

    const response = useAxios<ResponseData, HomeGainRequestPayload>({
        url: REQUEST_PATH,
        method: 'post',
        payload,
    });

    type MetricRawChangeValues = {
        raw?: number | null;
        originalRaw?: number | null;
        change?: number | null;
        originalChange?: number | null;
    }

    const data = useMemo(
        (): HomeGainDataItem[] => {

            return (response.data ?? []).map(item => {
                const itemBase = getKeywordBaseData(item.id ?? 0);

                const columns = new Map<string, KeywordTableColumn<number>>();
                const values: Record<AffinityAmong, MetricRawChangeValues> = {
                    [AffinityAmong.All]: {
                        raw: item.allConsumersRaw,
                        originalRaw: item.allConsumersOriginalRaw,
                        change: null,
                        originalChange: null,
                    },
                    [AffinityAmong.KnowIt]: {
                        raw: item.knowItRaw,
                        originalRaw: item.knowItOriginalRaw,
                        change: item.knowItDiff,
                        originalChange: item.knowItOriginalDiff,
                    },
                    [AffinityAmong.HaveTriedIt]: {
                        raw: item.haveTriedItRaw,
                        originalRaw: item.haveTriedItOriginalRaw,
                        change: item.haveTriedItDiff,
                        originalChange: item.haveTriedItOriginalDiff,
                    }
                };

                HOME_GAIN_AFFINITY_ORDER.forEach((among) => {
                    const isForAllConsumers = among === AffinityAmong.All;
                    const key = `${among}`;
                    const amongValueData = values[among];
                    const value = amongValueData.raw ?? null;
                    columns.set(key, {
                        id: item.id ?? 0,
                        name: '',
                        value,
                        tableValue: value !== null ? `${value}%` : '-',
                        copyTableValue: value !== null ? `${value}%` : '-',
                        sortValue: amongValueData.originalRaw ?? null,
                    });
                    if (!isForAllConsumers) {
                        const changeKey = `${among}Change`;
                        const changeValue = amongValueData.change ?? null;
                        columns.set(changeKey, {
                            id: item.id ?? 0,
                            name: '',
                            value: changeValue,
                            tableValue: changeValue !== null ? `${changeValue}%` : '-',
                            copyTableValue: changeValue !== null ? `${changeValue}%` : '-',
                            sortValue: amongValueData.originalChange ?? null,
                        });
                    }
                });

                return {
                    ...itemBase,
                    base: item.base ?? 0,
                    columns,
                };
            });
        },
        [response.data, getKeywordBaseData]
    );

    return {
        ...response,
        data,
    };
}